/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import styles from './modal.module.scss';

export interface ModalProps {
  /** The id of the element that names the dialog, e.g. its heading */
  'aria-labelledby'?: string;
  children: React.ReactNode;
  className?: string;
  isCentered?: boolean;
  /** Whether Escape and clicking outside close the modal, e.g. false while saving */
  isDismissible?: boolean;
  isOpen?: boolean;
  onClickOutside?: () => void;
  setIsOpen?: (isOpen: boolean) => void;
}

const Modal = ({
  'aria-labelledby': ariaLabelledBy,
  children,
  className,
  isCentered = false,
  isDismissible = true,
  isOpen,
  onClickOutside,
  setIsOpen,
}: ModalProps) => {
  const ref = useRef<HTMLDialogElement>(null);
  // The dialog listeners are added once, so they read the latest props from here
  const latestProps = useRef({
    isDismissible,
    isOpen,
    onClickOutside,
    setIsOpen,
  });

  useEffect(() => {
    latestProps.current = { isDismissible, isOpen, onClickOutside, setIsOpen };
  });

  useEffect(() => {
    const dialog = ref.current;

    if (
      !isOpen ||
      !dialog ||
      typeof dialog.showModal !== 'function' ||
      typeof dialog.close !== 'function'
    )
      return;

    // The dialog is unmounted when closed, so the browser never restores focus
    // to the element that opened it. Remember it and restore it ourselves.
    const previouslyFocusedElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    dialog.showModal();

    return () => {
      if (dialog.open) dialog.close();

      if (previouslyFocusedElement?.isConnected) {
        previouslyFocusedElement.focus();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    const dialog = ref.current;

    if (!dialog) return;

    const handleCancel = (event: Event) => {
      // Escape closes a dialog natively, so it's cancelled while it can't be dismissed
      if (!latestProps.current.isDismissible) {
        event.preventDefault();

        return;
      }

      latestProps.current.setIsOpen?.(false);
    };

    // Browsers ignore the cancelled Escape when it's repeated without user activation
    // in between, so a dialog that can't be dismissed is opened again
    const handleClose = () => {
      const { isDismissible: isCurrentlyDismissible, isOpen: isCurrentlyOpen } =
        latestProps.current;

      if (isCurrentlyOpen && !isCurrentlyDismissible && !dialog.open) {
        dialog.showModal();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (!ref.current || !latestProps.current.isDismissible) return;

      if (event.target === dialog) {
        latestProps.current.onClickOutside?.();
      }
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('close', handleClose);
    dialog.addEventListener('click', handleClickOutside);

    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('close', handleClose);
      dialog.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  if (!isOpen) return null;

  return (
    <dialog
      ref={ref}
      aria-labelledby={ariaLabelledBy}
      className={classNames(styles.component, className, {
        [styles.isCentered]: isCentered,
      })}
    >
      {children}
    </dialog>
  );
};

Modal.displayName = 'Modal';

export default Modal;
